
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  ArrowUpRight,
  Headset,
  ShieldCheck,
  MonitorCog,
  Code2,
  Network,
  Database,
  Wifi,
  ChevronRight,
  Cpu,
  Sparkles,
} from "lucide-react";

// ============================================================
// CONFIGURATION
// ============================================================

const API_URL = (import.meta.env.VITE_API_SOURCE_URL || "").replace(
  /\/+$/,
  ""
);

const OFFICE_ADDRESS =
  "Chandgaon Police Station, 1084, Shah Amanat Bridge Connecting Road, Chittagong 4212";

const COLORS = {
  primary: "#006b91",
  secondary: "#69cbed",
  dark: "#07536b",
  background: "#f4fbff",
};

// ============================================================
// IMAGE COMPONENT WITH FALLBACK
// ============================================================

function ProfileImage({ photo, name, className = "" }) {
  const [imageIndex, setImageIndex] = useState(0);

  const candidates = React.useMemo(() => {
    if (!photo) {
      return [
        "/images/default-officer.png",
        "/img/user-default.jpg",
      ];
    }

    const cleanPhoto = photo.replace(/^\/+/, "");
    const apiPhoto = API_URL ? `${API_URL}/${cleanPhoto}` : null;

    const paths = photo.startsWith("/img/")
      ? [photo, apiPhoto]
      : [apiPhoto, photo.startsWith("/") ? photo : `/${cleanPhoto}`];

    return [
      ...new Set([
        ...paths.filter(Boolean),
        "/images/default-officer.png",
        "/img/user-default.jpg",
      ]),
    ];
  }, [photo]);

  useEffect(() => {
    setImageIndex(0);
  }, [photo]);

  return (
    <img
      src={candidates[imageIndex] || "/img/user-default.jpg"}
      alt={name}
      loading="lazy"
      onError={() => {
        setImageIndex((current) =>
          current < candidates.length - 1 ? current + 1 : current
        );
      }}
      className={className}
    />
  );
}

// ============================================================
// TEAM DATA
// ============================================================

const senior = {
  name: "Md Yusuf Khalil",
  designation: "IT Consultant",
  office: "Information and Technology Division",
  mobile: "+8801979420300",
  email: "yusuf.khalil88@gmail.com",
  address: OFFICE_ADDRESS,
  photo: "assets/staff/Yusuf-Khalil.jpg",
  details: "#",
};

const systemAdministrator = {
  name: "Atikur Rahman",
  designation: "System Administrator",
  office: "Information and Technology Division",
  mobile: "+880 1313427307",
  email: "atik.it@uctc.edu.bd",
  address: OFFICE_ADDRESS,
  photo: "/img/Atikur Rahman.jpg",
  details: "#",
};

const officers = [
  {
    name: "Mehedi Hasan Asif",
    designation: "IT Officer",
    office: "Information and Technology Division",
    mobile: "+8801813244341",
    email: "asif@uctc.edu.bd",
    address: OFFICE_ADDRESS,
    photo: "assets/staff/Mahedi-Hasan-Asif.jpg",
    details: "#",
  },
  {
    name: "Md. Farhadul Islam Rana",
    designation: "IT Officer",
    office: "Information and Technology Division",
    mobile: "+8801313427307",
    email: "farhad.it@uctc.edu.bd",
    address: OFFICE_ADDRESS,
    photo: "/img/Rana.jpg",
    details: "#",
  },
  {
    name: "Nitu Chowdhury",
    designation: "Lab Assistant",
    office: "Information and Technology Division",
    mobile: "01866842071",
    email: "nitu@uctc.edu.bd",
    address: OFFICE_ADDRESS,
    photo: "assets/staff/nitu.jpg",
    details: "#",
  },
];

// ============================================================
// SERVICES
// ============================================================

const services = [
  {
    icon: MonitorCog,
    title: "IT Support",
    description:
      "Technical assistance for university staff, faculty members and students.",
  },
  {
    icon: Network,
    title: "Network & Connectivity",
    description:
      "Campus networking, internet access and infrastructure support.",
  },
  {
    icon: Database,
    title: "Systems & Databases",
    description:
      "Administration and maintenance of university information systems.",
  },
  {
    icon: ShieldCheck,
    title: "Security & Reliability",
    description:
      "System monitoring, access management and infrastructure reliability.",
  },
];

// ============================================================
// ANIMATIONS
// ============================================================

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 28,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

// ============================================================
// CONTACT ITEM
// ============================================================

function ContactItem({ icon: Icon, children, href, compact = false }) {
  const content = (
    <>
      <span
        className={`flex shrink-0 items-center justify-center rounded-xl bg-[#e8f8ff] text-[#006b91] ${
          compact ? "h-9 w-9" : "h-10 w-10"
        }`}
      >
        <Icon size={compact ? 16 : 18} />
      </span>

      <span className="min-w-0 flex-1 [overflow-wrap:anywhere] text-sm leading-6 text-slate-600">
        {children}
      </span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className="group flex min-w-0 items-start gap-3 rounded-xl transition-colors hover:text-[#006b91]"
      >
        {content}
      </a>
    );
  }

  return (
    <div className="flex min-w-0 items-start gap-3">
      {content}
    </div>
  );
}

// ============================================================
// LEADERSHIP PROFILE CARD
// ============================================================

function SeniorCard({ person, featured = false }) {
  return (
    <motion.article
      variants={fadeUp}
      className="group relative min-w-0 overflow-hidden rounded-3xl border border-[#e3f2f9] bg-white shadow-[0_15px_50px_-28px_rgba(0,90,130,0.25)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-25px_rgba(0,90,130,0.28)]"
    >
      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#007b9d] via-[#21c5d7] to-[#69cbed]" />

      <div
        className={`grid min-w-0 items-center gap-6 p-6 sm:p-8 ${
          featured
            ? "md:grid-cols-[220px_minmax(0,1fr)] lg:grid-cols-[250px_minmax(0,1fr)]"
            : "grid-cols-1"
        }`}
      >
        {/* Photograph */}
        <div
          className={`relative mx-auto w-full ${
            featured ? "max-w-[250px]" : "max-w-[200px]"
          }`}
        >
          <div className="absolute -inset-2 rounded-[2rem] bg-gradient-to-br from-[#69cbed]/40 via-[#c7f3ff] to-[#d7f8ee] blur-sm transition-opacity duration-500 group-hover:opacity-100" />

          <div className="relative overflow-hidden rounded-[1.6rem] border-4 border-white bg-[#eaf8ff] shadow-lg">
            <ProfileImage
              photo={person.photo}
              name={person.name}
              className={`w-full object-cover object-top transition-transform duration-700 group-hover:scale-105 ${
                featured ? "h-64 sm:h-72" : "h-60"
              }`}
            />
          </div>

          <div className="absolute -bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-[#dceff7] bg-white px-4 py-2 shadow-md">
            <span className="h-2 w-2 rounded-full bg-[#38bfa3]" />
            <span className="text-xs font-bold text-[#07536b]">
              ITD Team Member
            </span>
          </div>
        </div>

        {/* Information */}
        <div
          className={`min-w-0 pt-3 ${
            featured ? "md:pt-0" : "w-full"
          }`}
        >
          <span className="inline-flex max-w-full items-center gap-2 rounded-full bg-[#eafaf5] px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#087a70]">
            <Sparkles size={14} className="shrink-0" />
            <span>{featured ? "IT Leadership" : "Technical Team"}</span>
          </span>

          <h3 className="mt-4 break-words text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {person.name}
          </h3>

          <p className="mt-2 break-words text-base font-bold text-[#007d78]">
            {person.designation}
          </p>

          <p className="mt-1 break-words text-sm leading-6 text-slate-500">
            {person.office}
          </p>

          <div className="my-5 h-px bg-gradient-to-r from-[#cdeefa] via-slate-100 to-transparent" />

          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
            <ContactItem
              icon={Phone}
              href={`tel:${person.mobile.replace(/\s/g, "")}`}
            >
              {person.mobile}
            </ContactItem>

            <ContactItem
              icon={Mail}
              href={`mailto:${person.email}`}
            >
              {person.email}
            </ContactItem>

            <div className="min-w-0 sm:col-span-2">
              <ContactItem icon={MapPin}>
                {person.address}
              </ContactItem>
            </div>
          </div>

          {person.details && person.details !== "#" && (
            <a
              href={person.details}
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#006b91] px-5 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#07536b] hover:shadow-lg"
            >
              View Profile
              <ArrowUpRight size={16} />
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
}

// ============================================================
// OFFICER CARD
// ============================================================

function OfficerCard({ officer, index }) {
  return (
    <motion.article
      variants={fadeUp}
      whileHover={{ y: -6 }}
      className="group relative min-w-0 overflow-hidden rounded-3xl border border-[#e3f2f9] bg-white shadow-[0_12px_40px_-25px_rgba(0,90,130,0.25)] transition-shadow duration-300 hover:shadow-xl"
    >
      <div className="absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-[#006b91] via-[#21c5d7] to-[#69cbed]" />

      <div className="relative overflow-hidden bg-gradient-to-br from-[#eaf8ff] via-[#f3fcff] to-[#eafaf5] px-6 pb-3 pt-8">
        <div className="absolute -right-8 -top-10 h-36 w-36 rounded-full border-[18px] border-white/60" />

        <div className="absolute -bottom-12 -left-10 h-32 w-32 rounded-full bg-[#69cbed]/20 blur-2xl" />

        <div className="relative mx-auto h-48 w-40 overflow-hidden rounded-t-[5rem] rounded-b-2xl border-4 border-white bg-slate-100 shadow-lg">
          <ProfileImage
            photo={officer.photo}
            name={officer.name}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
          />
        </div>

        <div className="relative mt-4 flex justify-center">
          <span className="max-w-full rounded-full border border-[#dceff7] bg-white px-4 py-2 text-center text-xs font-bold text-[#006b91] shadow-sm">
            {officer.designation}
          </span>
        </div>
      </div>

      <div className="min-w-0 p-6 text-center">
        <h3 className="break-words text-xl font-extrabold tracking-tight text-slate-900">
          {officer.name}
        </h3>

        <p className="mt-2 break-words text-sm leading-6 text-slate-500">
          {officer.office}
        </p>

        <div className="mx-auto my-5 h-1 w-14 rounded-full bg-gradient-to-r from-[#69cbed] to-[#007d9f] transition-all duration-300 group-hover:w-24" />

        <div className="space-y-4 text-left">
          <ContactItem
            icon={Phone}
            compact
            href={`tel:${officer.mobile.replace(/\s/g, "")}`}
          >
            {officer.mobile}
          </ContactItem>

          <ContactItem
            icon={Mail}
            compact
            href={`mailto:${officer.email}`}
          >
            {officer.email}
          </ContactItem>

          <ContactItem icon={MapPin} compact>
            {officer.address}
          </ContactItem>
        </div>

        {officer.details && officer.details !== "#" && (
          <a
            href={officer.details}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#b9e7f6] px-4 py-3 text-sm font-bold text-[#006b91] transition-all hover:border-[#006b91] hover:bg-[#006b91] hover:text-white"
          >
            View Details
            <ArrowUpRight size={16} />
          </a>
        )}

        <div className="mt-5 flex items-center justify-center gap-2 text-xs font-medium text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full bg-[#38bfa3]" />
          Team Member {String(index + 1).padStart(2, "0")}
        </div>
      </div>
    </motion.article>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function Itd({
  accent = "#006b91",
  pageTitle =
    "Information and Technology Division | University of Creative Technology Chittagong",
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const scrollToTeam = () => {
    document.getElementById("itd-team")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main
      style={{
        "--itd-accent": accent,
      }}
      className="min-h-screen overflow-hidden bg-[#f4fbff] text-slate-800"
    >
      {/* ======================================================
          HERO
      ====================================================== */}

      <section className="relative isolate overflow-hidden bg-gradient-to-br from-[#07536b] via-[#087da5] to-[#69cbed] text-white">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_15%_10%,rgba(105,203,237,0.28),transparent_42%),radial-gradient(ellipse_at_90%_90%,rgba(255,255,255,0.17),transparent_40%)]" />

        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: "linear",
          }}
          className="pointer-events-none absolute -right-40 -top-56 -z-10 h-[500px] w-[500px] rounded-full border border-white/15"
        />

        <motion.div
          animate={{ rotate: -360 }}
          transition={{
            duration: 80,
            repeat: Infinity,
            ease: "linear",
          }}
          className="pointer-events-none absolute -right-20 -top-36 -z-10 h-[350px] w-[350px] rounded-full border border-dashed border-white/20"
        />

        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 sm:py-24 lg:grid-cols-[1.2fr_0.8fr] lg:px-12 lg:py-28">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div
              variants={fadeUp}
              className="mb-7 inline-flex max-w-full items-center gap-3 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-md"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white">
                <Cpu size={18} />
              </span>

              <span className="text-xs font-bold uppercase tracking-[0.12em] text-white sm:text-sm">
                University of Creative Technology Chittagong
              </span>
            </motion.div>

            <motion.p
              variants={fadeUp}
              className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-[#d7f7ff]"
            >
              Connecting People & Technology
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="max-w-3xl text-4xl font-black leading-[1.12] tracking-tight sm:text-5xl lg:text-7xl"
            >
              Information &{" "}
              <span className="text-[#d4f7ff]">Technology</span>{" "}
              Division
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-7 max-w-2xl text-base leading-8 text-white/85 sm:text-lg"
            >
              Empowering the university through innovative technology,
              reliable digital infrastructure, secure systems and responsive
              technical support.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mt-9 flex flex-wrap gap-4"
            >
              <button
                onClick={scrollToTeam}
                className="group inline-flex items-center gap-3 rounded-xl bg-white px-6 py-4 font-bold text-[#07536b] shadow-lg transition-all hover:-translate-y-1 hover:bg-[#eaf8ff]"
              >
                Meet Our Team
                <ArrowUpRight
                  size={19}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </button>

              <a
                href={`mailto:${senior.email}`}
                className="inline-flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-6 py-4 font-semibold text-white backdrop-blur transition-all hover:bg-white/20"
              >
                <Headset size={19} />
                Contact ITD
              </a>
            </motion.div>
          </motion.div>

          {/* Hero illustration */}
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.88,
              x: 30,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              x: 0,
            }}
            transition={{
              duration: 0.9,
              delay: 0.2,
            }}
            className="relative mx-auto w-full max-w-md"
          >
            <div className="absolute inset-5 rounded-[3rem] bg-white/20 blur-3xl" />

            <div className="relative overflow-hidden rounded-[2rem] border border-white/25 bg-white/10 p-5 shadow-2xl backdrop-blur-xl sm:p-7">
              <div className="flex items-center justify-between gap-4 border-b border-white/20 pb-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d4f7ff]">
                    Digital Campus
                  </p>

                  <h2 className="mt-2 text-xl font-extrabold">
                    Technology at Work
                  </h2>
                </div>

                <motion.div
                  animate={{
                    rotate: [0, 8, -8, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white"
                >
                  <MonitorCog size={29} />
                </motion.div>
              </div>

              <div className="relative my-7 flex h-48 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/15 to-white/5">
                <div className="absolute h-36 w-36 rounded-full border border-white/25" />
                <div className="absolute h-28 w-28 rounded-full border border-white/30" />

                <motion.div
                  animate={{
                    y: [0, -8, 0],
                    scale: [1, 1.04, 1],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative flex h-24 w-24 items-center justify-center rounded-3xl border border-white/30 bg-white/15 text-white shadow-2xl"
                >
                  <Code2 size={46} strokeWidth={1.5} />
                </motion.div>

                <motion.div
                  animate={{ y: [0, 7, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                  }}
                  className="absolute left-8 top-8 flex h-11 w-11 items-center justify-center rounded-xl border border-white/20 bg-white/15 text-white"
                >
                  <Wifi size={21} />
                </motion.div>

                <motion.div
                  animate={{ y: [0, -7, 0] }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                  }}
                  className="absolute bottom-7 right-8 flex h-11 w-11 items-center justify-center rounded-xl border border-white/20 bg-white/15 text-white"
                >
                  <Database size={21} />
                </motion.div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    icon: ShieldCheck,
                    title: "Secure Systems",
                    subtitle: "Protection first",
                  },
                  {
                    icon: Network,
                    title: "Connected Campus",
                    subtitle: "Reliable access",
                  },
                  {
                    icon: Database,
                    title: "Digital Services",
                    subtitle: "Smarter workflows",
                  },
                  {
                    icon: Headset,
                    title: "IT Assistance",
                    subtitle: "Here to help",
                  },
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 0.5 + index * 0.12,
                    }}
                    className="rounded-xl border border-white/15 bg-white/10 p-4 transition-colors hover:bg-white/20"
                  >
                    <item.icon className="mb-3 text-[#d4f7ff]" size={21} />

                    <p className="text-sm font-bold">{item.title}</p>

                    <p className="mt-1 text-xs text-white/75">
                      {item.subtitle}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/40" />
      </section>

      {/* ======================================================
          ABOUT
      ====================================================== */}

      <section className="mx-auto max-w-7xl px-5 pt-14 sm:px-8 lg:px-12">
        <motion.div
          initial={{
            opacity: 0,
            y: 25,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.25,
          }}
          transition={{ duration: 0.7 }}
          className="grid gap-7 rounded-3xl border border-[#dceff7] bg-white p-7 shadow-[0_20px_60px_-35px_rgba(0,90,130,0.25)] sm:p-10 lg:grid-cols-[1fr_auto] lg:items-center"
        >
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#006b91]">
              About Our Division
            </span>

            <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
              Technology that supports every ambition.
            </h2>

            <p className="mt-4 max-w-3xl leading-8 text-slate-600">
              The Information and Technology Division supports the
              university's digital environment through technical services,
              network administration, information systems and IT
              infrastructure. Our team works to help maintain a dependable,
              secure and connected campus.
            </p>
          </div>

          <div className="flex items-center gap-4 rounded-2xl bg-[#eaf8ff] p-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#006b91] text-white">
              <Headset size={27} />
            </div>

            <div>
              <p className="font-extrabold text-[#07536b]">
                Need IT Support?
              </p>

              <a
                href={`mailto:${senior.email}`}
                className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[#006b91] hover:underline"
              >
                Get in touch <ChevronRight size={15} />
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ======================================================
          SERVICES
      ====================================================== */}

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.2,
          }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="mb-10 text-center">
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#006b91]">
              What We Do
            </span>

            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
              Our IT Services
            </h2>

            <p className="mx-auto mt-4 max-w-2xl leading-7 text-slate-500">
              Supporting teaching, learning and administration through
              dependable technology services.
            </p>
          </motion.div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((service) => (
              <motion.article
                variants={fadeUp}
                key={service.title}
                whileHover={{ y: -6 }}
                className="group rounded-2xl border border-[#e3f2f9] bg-white p-6 shadow-sm transition-all duration-300 hover:border-[#a8e1f3] hover:shadow-xl"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eaf8ff] text-[#006b91] transition-all duration-300 group-hover:bg-[#006b91] group-hover:text-white">
                  <service.icon size={26} />
                </div>

                <h3 className="mt-5 text-lg font-extrabold text-slate-900">
                  {service.title}
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-500">
                  {service.description}
                </p>

                <div className="mt-5 h-1 w-10 rounded-full bg-[#bceafa] transition-all duration-300 group-hover:w-20 group-hover:bg-[#006b91]" />
              </motion.article>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ======================================================
          LEADERSHIP
      ====================================================== */}

      <section className="relative overflow-hidden bg-gradient-to-b from-[#f4fbff] via-[#eaf8ff] to-white py-20">
        <div className="pointer-events-none absolute -right-32 top-20 h-80 w-80 rounded-full bg-[#c8f0ff]/60 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.2,
            }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="mb-12 text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#006b91] shadow-sm ring-1 ring-[#dceff7]">
                <Sparkles size={14} />
                Meet Our Leadership
              </span>

              <h2 className="mt-5 text-3xl font-black tracking-tight text-slate-900 sm:text-5xl">
                Leading Our Digital Journey
              </h2>

              <p className="mx-auto mt-4 max-w-2xl leading-7 text-slate-500">
                Dedicated professionals working to strengthen the
                university's technology and digital infrastructure.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 items-start gap-7 lg:grid-cols-3">
              <div className="min-w-0 lg:col-span-2">
                <SeniorCard person={senior} featured />
              </div>

              <div className="min-w-0">
                <SeniorCard person={systemAdministrator} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          OTHER OFFICERS
      ====================================================== */}

      <section
        id="itd-team"
        className="scroll-mt-10 mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-12"
      >
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.15,
          }}
          variants={stagger}
        >
          <motion.div
            variants={fadeUp}
            className="mb-12 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between"
          >
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.28em] text-[#006b91]">
                The People Behind The Technology
              </span>

              <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-5xl">
                Meet Our IT Team
              </h2>

              <p className="mt-4 max-w-2xl leading-7 text-slate-500">
                Get to know the professionals who help keep our university
                connected, productive and digitally empowered.
              </p>
            </div>

            <div className="inline-flex w-fit shrink-0 items-center gap-2 rounded-xl border border-[#dceff7] bg-white px-4 py-3 text-sm font-bold text-[#07536b] shadow-sm">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#eaf8ff]">
                <Network size={17} />
              </span>

              {officers.length} Team Members
            </div>
          </motion.div>

          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {officers.map((officer, index) => (
              <OfficerCard
                key={officer.email}
                officer={officer}
                index={index}
              />
            ))}
          </div>
        </motion.div>
      </section>

      {/* ======================================================
          CONTACT BANNER
      ====================================================== */}

      <section className="px-5 pb-20 sm:px-8 lg:px-12">
        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.25,
          }}
          transition={{ duration: 0.7 }}
          className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#07536b] via-[#006b91] to-[#087da5] px-7 py-12 text-white shadow-2xl sm:px-12 sm:py-16"
        >
          <div className="absolute -right-20 -top-36 h-80 w-80 rounded-full border-[40px] border-white/[0.07]" />

          <div className="absolute -bottom-36 right-1/3 h-64 w-64 rounded-full bg-[#69cbed]/20 blur-3xl" />

          <div className="relative flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#d4f7ff]">
                <Headset size={18} />
                We Are Here to Help
              </div>

              <h2 className="mt-5 text-3xl font-black leading-tight sm:text-4xl">
                Need technical assistance?
              </h2>

              <p className="mt-4 leading-8 text-white/85">
                Contact the Information and Technology Division for
                assistance with university IT services and technical issues.
              </p>
            </div>

            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <a
                href={`mailto:${senior.email}`}
                className="inline-flex items-center justify-center gap-3 rounded-xl bg-white px-6 py-4 font-bold text-[#07536b] transition-all hover:-translate-y-1 hover:bg-[#eaf8ff]"
              >
                <Mail size={19} />
                Email ITD
                <ArrowUpRight size={17} />
              </a>

              <a
                href={`tel:${senior.mobile}`}
                className="inline-flex items-center justify-center gap-3 rounded-xl border border-white/30 px-6 py-4 font-bold text-white transition-all hover:bg-white/10"
              >
                <Phone size={19} />
                Call Us
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ======================================================
          FOOTER
      ====================================================== */}

      <footer className="border-t border-[#dceff7] bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-7 text-center sm:px-8 md:flex-row md:items-center md:justify-between md:text-left lg:px-12">
          <div>
            <p className="font-extrabold text-[#07536b]">
              Information and Technology Division
            </p>

            <p className="mt-1 text-sm text-slate-500">
              University of Creative Technology Chittagong
            </p>
          </div>

          <p className="text-xs leading-6 text-slate-400">
            Connecting people, systems and opportunities through technology.
          </p>
        </div>
      </footer>
    </main>
  );
}
